/**
 * FloatingCart Component
 * 
 * Floating action button for cart with item count and total
 */

import React from 'react';
import { ShoppingCart } from 'lucide-react';
import { useMenuTheme } from '@/hooks/useMenuTheme';
import { useCart } from '@/contexts/CartContext';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Cart } from '@/components/Cart';

interface FloatingCartProps {
  branchId: string;
  currency: string;
  isPreview?: boolean;
  className?: string;
}

export function FloatingCart({ branchId, currency, isPreview = false, className = '' }: FloatingCartProps) {
  const { customization } = useMenuTheme();
  const { totalItems, totalAmount } = useCart();
  const { t, i18n } = useTranslation();
  const { floatingCart } = customization.components;

  if (totalItems === 0 && !isPreview) {
    return null;
  }

  // Use dummy data for preview if cart is empty
  const displayItems = (isPreview && totalItems === 0) ? 1 : totalItems;
  const displayAmount = (isPreview && totalItems === 0) ? 15.00 : totalAmount;

  const getPositionClass = () => {
    switch (floatingCart.position) {
      case 'bottom-left': return 'bottom-4 start-4';
      case 'bottom-center': return 'bottom-4 left-1/2 -translate-x-1/2';
      case 'top-right': return 'top-4 end-4';
      default: return 'bottom-4 end-4'; // bottom-right
    }
  };

  const getSizeClasses = () => {
    switch (floatingCart.size) {
      case 'sm': return 'py-2 px-4 text-sm';
      case 'lg': return 'py-4 px-8 text-lg';
      default: return 'py-3 px-6';
    }
  };

  const getAnimationClass = () => {
    switch (floatingCart.animation) {
      case 'bounce': return 'animate-bounce';
      case 'pulse': return 'animate-pulse';
      default: return '';
    }
  };

  const getShapeClasses = () => {
    switch (floatingCart.buttonStyle) {
      case 'square': return 'rounded-lg';
      case 'pill': return 'rounded-full';
      case 'circle': return 'rounded-full aspect-square p-0 flex justify-center items-center';
      default: return 'rounded-full';
    }
  };

  return (
    <div
      className={`${isPreview ? 'absolute' : 'fixed'} ${getPositionClass()} z-20 transition-opacity duration-300 ${className}`}
    >
      <Dialog>
        <DialogTrigger asChild>
          <button
            className={`flex items-center gap-2 text-white font-bold shadow-lg transition-transform hover:scale-105 ${getSizeClasses()} ${getAnimationClass()} ${getShapeClasses()}`}
            style={{ 
              backgroundColor: floatingCart.buttonColor || customization.colors.primary,
              color: floatingCart.iconColor || '#ffffff'
            }}
          >
            <ShoppingCart className={floatingCart.size === 'sm' ? 'w-4 h-4' : floatingCart.size === 'lg' ? 'w-8 h-8' : 'w-6 h-6'} />
            
            {floatingCart.buttonStyle !== 'circle' && (
              <>
                {floatingCart.showItemCount && (
                  <span>{displayItems} {t('cart.items')}</span>
                )}
                
                {floatingCart.showItemCount && floatingCart.showTotalAmount && (
                  <span className="w-px h-6 bg-white/30"></span>
                )}
                
                {floatingCart.showTotalAmount && (
                  <span>{displayAmount.toFixed(2)} {currency}</span>
                )}
              </>
            )}
            
            {floatingCart.buttonStyle === 'circle' && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                {displayItems}
              </span>
            )}
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl w-[95%] h-[80vh] mx-auto flex flex-col" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('cart.title')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <Cart branchId={branchId} currency={currency} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
